/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/addons/addon_lora_helpers.test.ts
 */

//Hooks
import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { real_id } from '../interface_helpers';
//Stores
import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { device_notifications } from 'src/stores/features/device-notifications';
//Types
import type { Device } from '../../types/Device';
import type { LoraPeripheral, LoraPeripheralSettings, Peripheral } from 'src/types/Peripherals';
import type { DeviceNotification } from 'src/helpers/notifications/DeviceNotification';

const loraAddonTypes = {
	bytes_sent: {
		name: 'bytes_sent',
		icon: 'fas fa-arrow-up-from-line',
		unit: 'Bytes',
		label: 'Sent',
	},
	bytes_recd: {
		name: 'bytes_recd',
		icon: 'fas fa-arrow-down-from-line',
		unit: 'Bytes',
		label: 'Received',
	},
	air_time_hr_ms: {
		name: 'air_time',
		icon: 'fas fa-signal-stream',
		unit: '%',
		label: 'Air time',
	},
	air_time_total_ms: {
		name: 'time_total',
		icon: 'far fa-clock',
		unit: 'ms',
		label: 'Total air time',
	},
};

const LORA_NOTIFICATIONS = ['lora_duty_cycle_limit', 'lora_limit_reached', 'lora_send_blocked'];

export const getLoRaNotification = (device: Device): DeviceNotification | null => {
	const id = real_id(device?.id);
	const devNotifications = get(device_notifications);
	for (const key of LORA_NOTIFICATIONS) {
		const notification = devNotifications[`${id}-${key}`];
		if (notification) return notification;
	}
	return null;
};

export const getLoraAddonStatus = (device: Device, interface_data = []): Peripheral[] => {
	const id = real_id(device?.id),
		loraComponent = 'lora:100',
		loraStatus: LoraPeripheral = devices_status.getByRealId(id)?.[loraComponent],
		loraSettings: LoraPeripheralSettings = readDeviceSettings(id)?.[loraComponent];
	if (!loraStatus || !loraSettings) return interface_data;
	const $format = get(format);
	Object.keys(loraAddonTypes).forEach((key) => {
		if (
			loraStatus?.[key] != null &&
			!(loraSettings?.band_plan === 'US915' && key === 'air_time_hr_ms')
		) {
			const item = loraAddonTypes[key];
			const label = $format(`addon_peripheral_${item.name}`, {
				default: item.label,
			});
			const value = isNaN(loraStatus?.[key])
				? 'N/A'
				: getLoraAddonValue(parseFloat(loraStatus[key]), item);
			let iconColor = null;
			if (
				key === 'air_time_hr_ms' ||
				(key === 'bytes_sent' && loraSettings?.band_plan === 'US915')
			) {
				const loRaNotification = getLoRaNotification(device);
				if (loRaNotification) {
					iconColor = `--color-${loRaNotification.type}`;
				}
			}
			const peripheral = {
				name: item.name,
				peripheralType: 'lora',
				icon: item.icon,
				label,
				value,
				iconColor,
				sensor: {},
				settings: {},
				mainLabel: device.mode === 'lora' && key === 'air_time_hr_ms',
			};
			interface_data.push(peripheral);
		}
	});
	return interface_data;
};

export const isLoraPeripheralDevice = (deviceID: Device['id']): boolean => {
	return get(devices)?.[deviceID]?.category === 'lora';
};

export const deviceHasExtractedLoraPeripheral = (deviceID: Device['id']): Device => {
	return Object.values(get(devices)).find(
		(dev: Device) => real_id(dev.id) === deviceID && dev.category === 'lora',
	);
};

/**
 * Returns the device context where a LoRa-related notification should appear.
 * If the LoRa add-on is split into a separate peripheral device,
 * it will migrate its notifications to that device.
 *
 * @param {Device['id']} deviceId - Device ID to filter notifications for.
 * @param {string} notificationId - Notification ID
 * @returns {Device['id'] | null} - Target device id for this notification, or null to hide it.
 */
export const getLoraNotificationTargetDevice = (
	deviceId: Device['id'],
	notificationId: string,
): Device['id'] | null => {
	const [extractedId, notificationType] = notificationId.split('-', 2);
	if (LORA_NOTIFICATIONS.includes(notificationType)) {
		if (isLoraPeripheralDevice(deviceId)) {
			return extractedId;
		}
		if (deviceHasExtractedLoraPeripheral(extractedId)) {
			return null;
		}
	}
	return deviceId;
};

const formatBytes = (bytes: number) => {
	if (bytes === 0) {
		return '0 Bytes';
	}
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const step = Math.floor(Math.log(bytes) / Math.log(1024));
	const convertedValue = parseFloat((bytes / Math.pow(1024, step)).toFixed(2));
	return [convertedValue, sizes[step]].join(' ');
};

const getLoraAddonValue = (
	value: number,
	item: {
		name: string;
		unit: string;
	},
) => {
	if (/^bytes_/.test(item.name)) {
		return formatBytes(value);
	} else if (item.name == 'air_time') {
		const msInHour = 60 * 60 * 1000;
		return `${((value / msInHour) * 100).toFixed(1)} ${item.unit}`;
	}
	return `${value} ${item.unit}`;
};
